import React, { useEffect, useState } from 'react';
import StaticLayout from '@components/layout/StaticLayout';
import { IProposalDetailsPageProps } from '.';
import { useLazyQuery, useMutation } from '@apollo/client';
import {
  getNonSelectedPlanCover,
  // getNonSelectedPlanCover,
  getProposalDetails,
} from './queries';
import Loader from '@components/loader/Loader';
import { useParams } from 'react-router-dom';
import EnhancedStepper from '@components/stepper/EnhancedStepper';
import { getProposalStatus, LookupToList } from './utils';
import {
  convertToDetailsSummary,
  convertToPolicyPersons,
  convertToPolicyPlanProposalCovers,
  convertToProposalCover,
  getPlanCoversLov,
  IProposalDetailsPageResponse,
  IProposalDetailsSummary,
} from './custom-widgets/index2';
import PolicyDetailsEntityInfo from './custom-widgets/PolicyDetailsEntityInfo';
import { PolicyDetailsWidget } from './custom-widgets/PolicyDetailsWidget';
import {
  getProposalPageFormState,
  IProposalCover,
  IProposalPageFormState,
} from './custom-widgets/form';
import PolicyCoversRepeaterWidget from './custom-widgets/PolicyCoversRepeaterWidget';
import PolicyCostChargesWidget from './custom-widgets/PolicyCostChargesWidget';
import PolicyInsuredWidget from './custom-widgets/PolicyInsuredWidget';
import ProposalMotorDetailsWidget from './custom-widgets/ProposalMotorDetailsWidget';
import { validateProposalPageForm } from './validation';
import {
  convertProposalPageStateToSubmission,
  IProposalDetailsPageSubmissionModel,
} from './submission';
import {
  saveAndCalculationProposalPage,
  saveProposalDraftMotor,
} from './mutation';
import { toast } from 'react-toastify';
import ToastErrorMessage from '@components/toast/ToastErrorMessage';
import PolicyProposalQuoteWidget from './custom-widgets/PolicyProposalQuoteWidget';
import PreviewProposalClauseModal from './modals/PreviewProposalClauseModal';
import { isEmpty, isValidNumber } from '@utils/validationUtils';
import ToastSuccessMessage from '@components/toast/ToastSuccessMessage';
import { getError } from '@utils/graph-utils';
import { ComissionProposalWidget } from './custom-widgets/commission/ComissionProposalWidget';
import CloseProposalDrawer from '../close-proposal-drawer/CloseProposalDrawer';
import { downloadFileFromUrl, generateDownloadLink } from '@utils/file-utils';
import { downloadProposalDocument, issuePolicyMotor } from './modals/queries';
import { IConfirmation } from '@redux/confirmation/types';
import { OpenConfirmationAction } from '@redux/confirmation/actions';
import { useAppDispatch } from '@redux/hooks';
import { IAbstractRecord } from '@models';
import { IEnhancedMenuItem } from '@components/table/types';
import SimpleActionBar from '@components/menu/SimpleActionBar';
import { useStepperStyles, useTabsLayoutStyles } from '@styles/sharedStyles';

const ProposalDetailsPage: React.FC<IProposalDetailsPageProps> = () => {
  const { classes: tabClasses } = useTabsLayoutStyles();
  const { classes: stepperClasses } = useStepperStyles({
    mainContainerRatio: 8,
  });
  const dispatch = useAppDispatch();

  const [closeProposalDrawerOpen, setCloseProposalDrawerOpen] =
    useState<boolean>(false);
  const [issueModalOpen, setIssueModalOpen] = useState<boolean>(false);

  const [submitting, setSubmitting] = useState<boolean>(false);
  const [loading, setLoading] = useState<boolean>(true);
  const params = useParams();
  const entityId = params.id;

  const [pageState, onPageStateUpdate] = useState<IProposalPageFormState>(
    getProposalPageFormState()
  );

  const [data, setData] = useState<IProposalDetailsSummary>();

  // const ownerIsDifferent: boolean =
  //   pageState?.values?.policyPersons?.payer?.ownerIsDifferent || false;

  const [coversList, setCoversList] = useState<IProposalCover[]>([]);

  const [planCoversList, setPlanCoversList] = useState<
    Record<string, IProposalCover>
  >({});

  const [lovs, setLovs] = useState<Record<string, Record<string, string>>>({
    planCovers: {},
  });

  const [saveAndCalculationAction] = useMutation(
    saveAndCalculationProposalPage()
  );

  const [saveAsDraftAction] = useMutation(saveProposalDraftMotor());

  const [issuePolicyMotorAction] = useMutation(issuePolicyMotor());

  const [downloadAction] = useMutation(downloadProposalDocument());

  useEffect(() => {
    initialize();
  }, []);

  const isClosed =
    data?.Status === 'CLOSED_WON' || data?.Status === 'CLOSED_LOST';

  const disabledPage = isClosed || submitting || loading;

  const [getProposalDetailsLazy] = useLazyQuery(getProposalDetails());

  const [getNonSelectedPlanCoverLazy] = useLazyQuery(getNonSelectedPlanCover());

  const getPlanCoverDetails = async (
    planId: string,
    planCoverIds: string[]
  ) => {
    const { data } = await getNonSelectedPlanCoverLazy({
      variables: {
        selectedPlanID: planId,
        selectedPlanCoverIDs: planCoverIds,
      },
      errorPolicy: 'all',
      fetchPolicy: 'no-cache',
    });

    return data;
  };

  const initialize = async () => {
    setLoading(true);
    const result: { data: IProposalDetailsPageResponse } =
      await getProposalDetailsLazy({
        variables: { id: entityId },
        errorPolicy: 'all',
        fetchPolicy: 'no-cache',
      });

    const summaryData = convertToDetailsSummary(result.data);

    const initialCovers = convertToPolicyPlanProposalCovers(result.data);
    const initialPersons = convertToPolicyPersons(result.data);

    const planCoverDetails = await getPlanCoverDetails(
      summaryData.PlanID.Id,
      []
    );

    const lists = LookupToList(result.data);
    setLovs({
      plateCodes: lists.plateCodes,
      usageTypes: lists.usageTypes,
      bodyTypes: lists.bodyTypes,
      engineTypes: lists.engineTypes,
      brands: lists.brands,
      colors: lists.colors,
      planCovers: getPlanCoversLov(planCoverDetails),
    });

    setPlanCoversList(
      convertToProposalCover(
        planCoverDetails,
        summaryData.PolicyCurrency?.Code,
        summaryData.PricingOption.PlanToPolicyRate,
        summaryData.PolicyEffectiveDate,
        summaryData.PolicyExpiryDate
      )
    );
    setCoversList(initialCovers);
    onPageStateUpdate(
      getProposalPageFormState(summaryData, initialCovers, initialPersons)
    );
    setData(summaryData);
    setLoading(false);
  };

  const renderLeftSection = () => {
    if (data) {
      return <PolicyDetailsEntityInfo data={data} />;
    }

    return <></>;
  };

  const onSubmit = async () => {
    const validationResult = validateProposalPageForm(data, pageState.values);

    if (!submitting) {
      setSubmitting(true);
      const newPageState = {
        ...pageState,
        errors: validationResult.errors,
        touched: validationResult.touched,
      };

      onPageStateUpdate(newPageState);
      if (validationResult.isValid) {
        const dataToSubmit = convertProposalPageStateToSubmission(
          data,
          pageState
        );
        const result = await saveAndCalculationAction({
          variables: { ...dataToSubmit },
          errorPolicy: 'all',
        });

        if (isEmpty(result.errors)) {
          // setDeactivateButtonState("success");
          toast.success(
            <ToastSuccessMessage>
              {'Proposal successfully saved.'}
            </ToastSuccessMessage>
          );

          initialize();
        } else {
          // setRegenerateButtonState(undefined);
          toast.error(
            <ToastErrorMessage>{getError(result)}</ToastErrorMessage>
          );
        }
      } else {
        toast.error(<ToastErrorMessage>Incomplete Form</ToastErrorMessage>);
      }
      setSubmitting(false);
    }
  };

  const onSaveDraft = async () => {
    if (!submitting) {
      setSubmitting(true);
      const dataToSubmit = convertProposalPageStateToSubmission(
        data,
        pageState
      ) as Partial<IProposalDetailsPageSubmissionModel>;

      if (isEmpty(dataToSubmit.policyPersonInput[0]?.personId)) {
        dataToSubmit.policyPersonInput = [];
      }

      const result = await saveAsDraftAction({
        variables: { ...dataToSubmit },
        errorPolicy: 'all',
      });

      if (isEmpty(result.errors)) {
        // setDeactivateButtonState("success");
        toast.success(
          <ToastSuccessMessage>
            {'Proposal successfully saved.'}
          </ToastSuccessMessage>
        );

        initialize();
      } else {
        // setRegenerateButtonState(undefined);
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    }
    setSubmitting(false);
  };

  const onIssuePolicyAction = async () => {
    if (!submitting) {
      setSubmitting(true);
      const dataToSubmit = {
        entityId: data.Id,
        clauseText: data.ClauseText || '',
      };
      const result = await issuePolicyMotorAction({
        variables: { ...dataToSubmit },
        errorPolicy: 'all',
      });

      if (isEmpty(result.errors)) {
        // setDeactivateButtonState("success");
        toast.success(
          <ToastSuccessMessage>
            {'Policy successfully issued.'}
          </ToastSuccessMessage>
        );

        const filesInfo = getFileDetailsList(result.data);

        for (const fileInfo of filesInfo) {
          const downloadLink = generateDownloadLink(
            fileInfo.fileId,
            fileInfo.EntityTypeId,
            fileInfo.EntityId,
            fileInfo.EntityViewId,
            fileInfo.PropertyId
          );
          await downloadFileFromUrl(downloadLink, fileInfo.fileName);
        }
        setSubmitting(false);
        initialize();
      } else {
        // setRegenerateButtonState(undefined);
        setSubmitting(false);
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    }
  };

  const downloadDocument = async () => {
    if (!submitting) {
      setSubmitting(true);
      const dataToSubmit = {
        entityId: data.Id,
        clauseText: data.ClauseText,
      };
      const result = await downloadAction({
        variables: { ...dataToSubmit },
        errorPolicy: 'all',
      });
      if (isEmpty(result.errors)) {
        // setDeactivateButtonState("success");
        const fileInfo = getFileDetails(result.data);
        const downloadLink = generateDownloadLink(
          fileInfo.fileId,
          fileInfo.EntityTypeId,
          fileInfo.EntityId,
          fileInfo.EntityViewId,
          fileInfo.PropertyId
        );

        await downloadFileFromUrl(downloadLink, fileInfo.fileName);

        toast.success(
          <ToastSuccessMessage>
            {'Policy sample successfully downloaded.'}
          </ToastSuccessMessage>
        );
        // onSuccess();
      } else {
        // setRegenerateButtonState(undefined);
        setSubmitting(false);
        toast.error(<ToastErrorMessage>{getError(result)}</ToastErrorMessage>);
      }
    }

    setSubmitting(false);
  };

  const renderActions = () => {
    const actions: IEnhancedMenuItem[] = [
      {
        name: 'saveDraft',
        title: 'Save Draft',
        onClick: () => {
          onSaveDraft();
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden:
          isClosed ||
          data?.Status?.toLowerCase() !== 'new' ||
          !(
            isValidNumber(data?.LineId?.ExternalCode) &&
            [51, 50, 4].includes(Number(data.LineId.ExternalCode))
          ),
      },
      {
        name: 'calculateAndSave',
        title: 'Calculate & Save',
        onClick: () => {
          onSubmit();
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden:
          isClosed ||
          !(
            isValidNumber(data?.LineId?.ExternalCode) &&
            [51, 50, 4].includes(Number(data.LineId.ExternalCode))
          ),
      },
      {
        name: 'issuePolicy',
        title: 'Issue Policy',
        onClick: () => {
          if (Number(data?.LineId?.ExternalCode) === 50) {
            const confirmation: IConfirmation = {
              open: true,
              title: 'Confirmation',
              message: `Are you sure you want to issue the selected policy?`,
              callback: async () => {
                onIssuePolicyAction();
              },
              submitButtonText: 'Yes',
              cancelButtonText: 'No',
            };
            dispatch(OpenConfirmationAction(confirmation));
          } else {
            setIssueModalOpen(true);
          }
        },
        isPrimary: true,
        disabled: disabledPage,
        hidden:
          isClosed ||
          !(
            data?.Status?.toLowerCase() === 'in_progress' &&
            isValidNumber(data?.LineId?.ExternalCode) &&
            [51, 50, 4].includes(Number(data.LineId.ExternalCode))
          ),
      },
      {
        name: 'downloadPolicySample',
        title: 'Download Policy Sample',
        onClick: () => {
          downloadDocument();
        },
        isSecondary: data?.LineId?.ExternalCode === '50',
        hidden:
          isClosed ||
          !(
            data?.Status?.toLowerCase() === 'in_progress' &&
            data.LineId.ExternalCode === '50'
          ),
        disabled: disabledPage,
      },
      {
        name: 'closeProposal',
        title: 'Close Proposal',
        onClick: () => {
          setCloseProposalDrawerOpen(true);
        },
        isPrimary: data?.LineId?.ExternalCode !== '50',
        disabled: disabledPage,
        hidden: isClosed,
      },
    ];

    return <SimpleActionBar items={actions} />;
  };

  const renderMainChildren = () => {
    if (data) {
      // const isClosed = status === "closedwon" || "closedlost";
      const isProposalWon = data.Status?.toUpperCase() === 'CLOSED_WON';
      const isProposalUserNew = data.Status?.toLowerCase() === 'new';
      const steps = isProposalUserNew
        ? ['New', 'In Progress', 'Closed Won']
        : ['New', 'In Progress', isProposalWon ? 'Closed Won' : 'Closed Lost'];

      return (
        <section>
          <div className={stepperClasses.stepperContainer}>
            <div className={stepperClasses.stepperWrapper}>
              <EnhancedStepper
                activeStep={getProposalStatus(steps, data.Status)}
                steps={steps}
                buttonTitle="Calculate & Save"
                buttonOnClick={() => undefined}
              />
            </div>
            <div className={stepperClasses.actionsWrapper}>
              {renderActions()}
            </div>
          </div>
          <div className={tabClasses.tabSplitBody}>
            <div className={tabClasses.tabMainBody}>
              <PolicyDetailsWidget data={data} />
              <PolicyCoversRepeaterWidget
                defaultPlanCoversList={planCoversList}
                pageState={pageState}
                disabledForm={disabledPage}
                status={data.Status}
                allowPlanCoverEdits={data.BusinessUserID.AllowPlanCoverEdits}
                onPageStateUpdate={onPageStateUpdate}
                lovs={lovs}
                data={data}
              />
              <PolicyCostChargesWidget
                pageState={pageState}
                disabledForm={disabledPage}
                status={data.Status}
                editableLine={data.BusinessUserID?.EditableLineChargesIDs?.includes(
                  data?.LineId.Id
                )}
                line={data?.LineId.ExternalCode?.toString() || ''}
                onPageStateUpdate={onPageStateUpdate}
                currencySymbol={data.PolicyCurrency.Symbol}
              />

              <PolicyInsuredWidget
                pageState={pageState}
                onPageStateUpdate={onPageStateUpdate}
                disabledForm={disabledPage}
                maxNumber={data.PlanID.MaxNbInsured}
                data={data}
              />

              <ProposalMotorDetailsWidget
                pageState={pageState}
                onPageStateUpdate={onPageStateUpdate}
                lovs={lovs}
                data={data}
                disabledForm={disabledPage}
              />
              {closeProposalDrawerOpen && (
                <CloseProposalDrawer
                  open={closeProposalDrawerOpen}
                  onClose={() => {
                    setCloseProposalDrawerOpen(false);
                  }}
                  onSuccess={() => {
                    initialize();
                    setCloseProposalDrawerOpen(false);
                  }}
                  id={entityId}
                />
              )}
            </div>
            <div className={tabClasses.tabRightSection}>
              <PolicyProposalQuoteWidget data={data} covers={coversList} />
              {data?.Status && data?.Status?.toLowerCase() !== 'new' && (
                <ComissionProposalWidget
                  data={data}
                  onUpdateSuccess={() => {
                    initialize();
                  }}
                />
              )}
            </div>
          </div>
        </section>
      );
    }

    return <></>;
  };

  return loading ? (
    <Loader />
  ) : (
    <>
      <StaticLayout
        name={'Proposal Details'}
        leftChildren={renderLeftSection()}
        mainChildren={renderMainChildren()}
      />
      {issueModalOpen && (
        <PreviewProposalClauseModal
          data={data}
          onSuccess={() => {
            setIssueModalOpen(false);
            initialize();
          }}
          onClose={() => setIssueModalOpen(false)}
        />
      )}
    </>
  );
};

export default ProposalDetailsPage;

function getFileDetailsList(data: IAbstractRecord): FileDetail[] {
  const fileDetails: FileDetail[] = [];

  const items =
    data.production.entities.proposal.production.issuePolicyMotor.Items;
  for (const item of items) {
    const fileDetail: FileDetail = {
      EntityTypeId: item.EntityTypeId,
      EntityId: item.EntityId,
      EntityViewId: item.EntityViewId,
      PropertyId: item.PropertyId,
      fileId: item.File.id,
      fileName: item.File.fileName,
    };
    fileDetails.push(fileDetail);
  }

  return fileDetails;
}

function getFileDetails(data: IAbstractRecord) {
  const downloadPolicySampleMotor =
    data.production.entities.proposal.production.downloadPolicySampleMotor;
  const { EntityTypeId, EntityId, EntityViewId, PropertyId, File } =
    downloadPolicySampleMotor;
  const { id: fileId, fileName } = File;

  return {
    EntityTypeId,
    EntityId,
    EntityViewId,
    PropertyId,
    fileId,
    fileName,
  };
}

type FileDetail = {
  EntityTypeId: string;
  EntityId: string;
  EntityViewId: string;
  PropertyId: string;
  fileId: string;
  fileName: string;
};
